import { computed, makeObservable, observable } from "mobx";

import { parseCss, parseHtml } from "@use/parse-html";
import { ReactHtmlParser } from "@use/react-html-parser";

import { BlockData, BlockModel, BlockRemoteData } from "@courses-models/course/src/ts/models/BlockModel";

/**
 * Specification of raw data required to create a new EmbedBlockModel.
 */
type EmbedBlockData = BlockData & {
    embedCode: string;
};

/**
 * Specification of the format used when serializing embed block content for
 * storage in the remote backend.
 */
type EmbedBlockRemoteData = {
    embedCode: string;
};

/**
 * Create an html parser instance specifically for this model, which we're going
 * to use to convert the input code to the desired output.
 */
const htmlParser = new ReactHtmlParser();

// Allow only embedding of iframes!
htmlParser.whitelistTags("iframe");
htmlParser.blacklistTextNodes();

/**
 * Embed blocks contain an embed code entered by the teacher, that should be
 * injected into the actual frontend.
 */
export class EmbedBlockModel extends BlockModel {
    /**
     * Contains the embed code as entered by the teacher.
     */
    public embedCode: string;

    /**
     * Apply initial data when the model is instantiated.
     */
    constructor(initial: EmbedBlockData) {
        super(initial);

        this.embedCode = initial.embedCode;

        makeObservable(this, {
            title: observable,
            note: observable,
            embedCode: observable,
            src: computed,
            desiredHeight: computed,
            desiredWidth: computed,
        });
    }

    /**
     * Helper that'll create an instance of the EmbedBlockModel based on a
     * serialized data from the remote backend.
     */
    public static DESERIALIZE(data: BlockRemoteData): EmbedBlockModel {
        const content = <EmbedBlockRemoteData>JSON.parse(data.content);

        return new EmbedBlockModel({
            id: data.id,
            note: {
                content: data.note.content,
                enabled: data.note.isVisible,
            },
            title: data.title,
            ...content,
        });
    }

    /**
     * Parses the embed code, and extracts the src-tag from it so that it is
     * easily available externally.
     */
    public get src(): string | undefined {
        const node = this.getFirstNode();

        // Bail out if no node was received
        if (!node) {
            return undefined;
        }

        // ... Otherwise extract src prop from the node
        const src = node.getAttribute("src");

        return (
            src !== null
                ? src
                : undefined
        );
    }

    /**
     * Parses the embed code, and extracts the desired height of the embedded
     * element.
     */
    public get desiredHeight(): number | undefined {
        const node = this.getFirstNode();

        // Bail out if no node was received
        if (!node) {
            return undefined;
        }

        // Extract height and style attributes from the node
        const heightAttr = node.getAttribute("height");
        const styleAttr = node.getAttribute("style");

        // If a custom styling was given, then we'll need to extract height
        // from there
        if (styleAttr) {
            const css = parseCss(styleAttr);

            if (css) {
                // Extract height from css styling
                const height = css.height;

                // If a height was found, then prefer this value...
                if (height) {
                    return cssLengthToPx(height);
                }
            }
        }

        // If we get here, fall back to returning the value from the height
        // attribute
        return (
            heightAttr
                ? parseInt(heightAttr, 10)
                : undefined
        );
    }

    /**
     * Parses the embed code, and extracts the desired width of the embedded
     * element.
     */
    public get desiredWidth(): number | undefined {
        const node = this.getFirstNode();

        // Bail out if no node was received
        if (!node) {
            return undefined;
        }

        // Extract width and style attributes from the node
        const widthAttr = node.getAttribute("width");
        const styleAttr = node.getAttribute("style");

        // If a custom styling was given, then we'll need to extract width
        // from there
        if (styleAttr) {
            const css = parseCss(styleAttr);

            if (css) {
                // Extract width from css styling
                const width = css.width;

                // If a width was found, then prefer this value...
                if (width) {
                    return cssLengthToPx(width);
                }
            }
        }

        // If we get here, fall back to returning the value from the width
        // attribute
        return (
            widthAttr
                ? parseInt(widthAttr, 10)
                : undefined
        );
    }

    /**
     * Helper that converts the embed code into a single React <iframe ... />
     * node, so that we avoid risks of XSS.
     */
    public async getIframe(options?: { quiet?: boolean }): Promise<React.ReactNode | undefined> {
        const output = await htmlParser.parse(this.embedCode, options);

        return output[0];
    }

    /**
     * Helper that'll return a serialized version of the content, so that it can
     * be stored remotely.
     */
    public serializeContent(): string {
        return JSON.stringify({ embedCode: this.embedCode });
    }

    /**
     * Helper that extracts a reference to the first node within the embed code.
     */
    protected getFirstNode(): Element | undefined {
        // Parse the given embed code into a DOM node
        const node = parseHtml(this.embedCode.trim())
            .childNodes
            .item(0);

        // Bail out if no node was received
        if (!isElement(node)) {
            return undefined;
        }

        return node;
    }

    /**
     * Helper that'll generate a snapshot of the content within this block
     */
    protected getContentSnapshot(): string {
        return this.embedCode;
    }
}

/**
 * Typeguard that verifies that the node extracted from the embed code is
 * actually a DOM element.
 */
function isElement(node?: ChildNode): node is Element {
    return !!node && node.nodeType === node.ELEMENT_NODE;
}

/**
 * Helper that'll convert absolute css lengths to their css pixel values, but
 * returns undefined for relative lengths.
 */
function cssLengthToPx(length: string): number | undefined {
    const value = parseInt(
        length
            .replace(/[^\d]/g, "")
            .trim(),
        10,
    );

    const unit = length.replace(/\d/g, "")
        .trim()
        .toLowerCase();

    switch (unit) {
        case "px":
            return value;

        case "pt":
            return value * 1.3333;

        case "pc":
            return value * 16;

        case "in":
            return value * 96;

        case "cm":
            return value * 37.8;

        case "mm":
            return value * 3.78;

        default:
            // All other units are relative...
            return undefined;
    }
}
